@if ((isAuthenticated$ | async) && enabled()) {
  <button
    type="button"
    class="btn btn-primary rounded-pill help-launcher d-print-none"
    [style.transform]="launcherTransform()"
    (pointerdown)="startLauncherDrag($event)"
    (click)="toggleFromLauncher()"
    i18n-title
    title="Ask Stan (drag to move)"
    i18n-aria-label
    aria-label="Ask Stan, the TMSWeb assistant"
  >
    <span class="fa fa-question-circle me-2"></span><span i18n>Ask Stan</span>
  </button>

  @if (open()) {
    <div
      class="help-panel card shadow d-print-none"
      [class.help-panel-expanded]="expanded()"
      [style.transform]="panelTransform()"
    >
      <div
        class="card-header bg-dark text-white d-flex align-items-center help-drag-handle"
        (pointerdown)="startPanelDrag($event)"
      >
        <span class="fa fa-question-circle me-2"></span>
        <span i18n>Stan — TMSWeb Assistant</span>
        <button
          type="button"
          class="btn btn-sm btn-link text-white ms-auto"
          (click)="toggleExpanded()"
          i18n-title
          title="Expand or shrink the panel"
          [attr.aria-pressed]="expanded()"
        >
          <span class="fa" [class.fa-expand-alt]="!expanded()" [class.fa-compress-alt]="expanded()"></span>
        </button>
        @if (speechSupported) {
          <button
            type="button"
            class="btn btn-sm btn-link text-white"
            (click)="toggleSpeakReplies()"
            i18n-title
            title="Read replies aloud"
            [attr.aria-pressed]="speakReplies()"
          >
            <span class="fa" [class.fa-volume-up]="speakReplies()" [class.fa-volume-mute]="!speakReplies()"></span>
          </button>
        }
        @if (messages.length > 0) {
          <button type="button" class="btn btn-sm btn-link text-white" (click)="clear()" i18n>Clear</button>
        }
        <button
          type="button"
          class="btn-close btn-close-white"
          (click)="togglePanel()"
          i18n-aria-label
          aria-label="Close"
        ></button>
      </div>

      <div class="card-body help-messages" #messageList (click)="onMessageClick($event)">
        @if (messages.length === 0) {
          <p class="text-muted">
            @if (firstName$ | async; as firstName) {
              <span i18n>
                Hi {{ firstName }}, I'm Stan. Ask me how to do something in TMSWeb — for example "How do I enter a sales
                order?"
              </span>
            } @else {
              <span i18n>
                Hi, I'm Stan. Ask me how to do something in TMSWeb — for example "How do I enter a sales order?"
              </span>
            }
          </p>
        }
        @for (message of messages; track $index) {
          <div
            class="help-message"
            [class.bg-primary]="message.role === roles.user"
            [class.text-white]="message.role === roles.user"
            [class.ms-auto]="message.role === roles.user"
            [class.bg-light]="message.role !== roles.user"
            [innerHTML]="messageHtml[$index]"
          ></div>
        }
        @if (draft(); as draftOrder) {
          <div class="help-draft card border-primary">
            <div class="card-body">
              <div class="fw-bold" i18n>Draft sales order</div>
              <div class="mb-2">{{ draftOrder.customerName }}</div>
              <table class="table table-sm help-draft-lines">
                <thead>
                  <tr>
                    <th i18n>Product</th>
                    <th class="text-end" i18n>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  @for (line of draftOrder.lines; track $index) {
                    <tr>
                      <td>{{ line.productKey }} — {{ line.description }}</td>
                      <td class="text-end">{{ line.quantity }}</td>
                    </tr>
                  }
                </tbody>
              </table>
              <div class="d-flex gap-2 justify-content-end">
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="discardDraft()" i18n>
                  Discard
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  (click)="createDraftOrder()"
                  [disabled]="busy()"
                  i18n
                >
                  Create order
                </button>
              </div>
            </div>
          </div>
        }
        @if (purchaseDraft(); as draftOrder) {
          <div class="help-draft card border-primary">
            <div class="card-body">
              <div class="fw-bold" i18n>Draft purchase order</div>
              <div class="mb-2">{{ draftOrder.supplierName }}</div>
              <table class="table table-sm help-draft-lines">
                <thead>
                  <tr>
                    <th i18n>Product</th>
                    <th class="text-end" i18n>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  @for (line of draftOrder.lines; track $index) {
                    <tr>
                      <td>{{ line.productKey }} — {{ line.description }}</td>
                      <td class="text-end">{{ line.quantity }}</td>
                    </tr>
                  }
                </tbody>
              </table>
              <div class="d-flex gap-2 justify-content-end">
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="discardDraft()" i18n>
                  Discard
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  (click)="createDraftPurchaseOrder()"
                  [disabled]="busy()"
                  i18n
                >
                  Create order
                </button>
              </div>
            </div>
          </div>
        }
        @if (busy()) {
          <div class="help-message bg-light text-muted" i18n>Thinking…</div>
        }
      </div>

      @if (warningPercent(); as percent) {
        <div class="help-usage-warning small text-warning-emphasis bg-warning-subtle px-3 py-1" i18n>
          This site has used {{ percent }}% of its monthly assistant allowance.
        </div>
      }

      <div class="card-footer d-flex gap-2">
        @if (dictationSupported) {
          <button
            type="button"
            class="btn"
            [class.btn-danger]="listening()"
            [class.help-mic-listening]="listening()"
            [class.btn-outline-secondary]="!listening()"
            (click)="toggleListening()"
            [disabled]="busy()"
            i18n-title
            title="Dictate your question"
            [attr.aria-pressed]="listening()"
          >
            <span class="fa fa-microphone"></span>
          </button>
        }
        <input
          type="text"
          class="form-control"
          [(ngModel)]="question"
          (keyup.enter)="send()"
          [disabled]="busy()"
          i18n-placeholder
          placeholder="Ask a question…"
          i18n-aria-label
          aria-label="Your question"
        />
        <button
          type="button"
          class="btn btn-primary"
          (click)="send()"
          [disabled]="busy() || !question.trim()"
          i18n-aria-label
          aria-label="Send"
        >
          <span class="fa fa-paper-plane"></span>
        </button>
      </div>
    </div>
  }
}
